import React from 'react';
import ChildCheckboxRow from './ChildCheckboxRow';

const RadioDot = ({ selected, accentBorder, accentBg }) => (
  <div className={`w-[26px] h-[26px] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
    selected ? accentBorder : 'border-border-gray'
  } ${selected ? accentBg : 'bg-white'}`}>
    {selected && <div className="w-[9px] h-[9px] rounded-full bg-white" />}
  </div>
);

const WhoseStorySelector = ({
  tier,
  onTierChange,
  children: childrenList,
  selectedChildIds,
  onToggleChild,
  onToggleAll,
  accentClasses,
}) => {
  const allSelected =
    childrenList.length > 0 && selectedChildIds.length === childrenList.length;

  // Get initials for avatar
  const getInitials = (child) =>
    [child.first_name, child.last_name]
      .filter(Boolean)
      .map(n => n.charAt(0).toUpperCase())
      .join('');

  return (
    <div className="space-y-3">
      {/* ── Tier 1: My Story ──────────────────────────────────── */}
      <button
        type="button"
        onClick={() => onTierChange('mine')}
        role="radio"
        aria-checked={tier === 'mine'}
        className={`w-full flex items-center gap-4 border-2 rounded-2xl p-5 cursor-pointer text-left transition-all duration-150 ${
          tier === 'mine'
            ? 'border-primary-green bg-green-50'
            : 'border-border-gray bg-white'
        }`}
      >
        {/* Avatar */}
        <div className="w-14 h-14 sm:w-12 sm:h-12 rounded-full bg-primary-green flex items-center justify-center text-white text-lg font-bold flex-shrink-0">
          N
        </div>
        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="font-serif text-xl font-semibold text-text-dark">My story</p>
          <p className="text-lg text-text-medium">Saved into my own life story</p>
        </div>
        {/* Radio dot */}
        <RadioDot
          selected={tier === 'mine'}
          accentBorder="border-primary-green"
          accentBg="bg-primary-green"
        />
      </button>

      {/* ── Tier 2: My Child's Story ──────────────────────────── */}
      <div
        role="radio"
        aria-checked={tier === 'child'}
        className={`border-2 rounded-2xl transition-all duration-150 overflow-hidden ${
          tier === 'child'
            ? `${accentClasses.border} ${accentClasses.bg}`
            : 'border-border-gray bg-white'
        }`}
      >
        <button
          type="button"
          onClick={() => onTierChange('child')}
          className="w-full flex items-center gap-4 p-5 cursor-pointer text-left"
        >
          {/* Stacked child avatars */}
          <div className="flex flex-shrink-0">
            {childrenList.length > 0 ? (
              childrenList.map((child, idx) => {
                const isSelected = selectedChildIds.includes(child.id);
                const bgColor = child.accentColor === 'blue' ? 'bg-child-blue' : 'bg-highlight-orange';
                return (
                  <div
                    key={child.id}
                    className={`w-14 h-14 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white text-sm font-bold border-2 border-white ${bgColor} ${
                      idx > 0 ? '-ml-3' : ''
                    } ${tier === 'child' && !isSelected ? 'opacity-30' : ''} transition-opacity`}
                  >
                    {getInitials(child) || '?'}
                  </div>
                );
              })
            ) : (
              <div className="w-14 h-14 sm:w-12 sm:h-12 rounded-full bg-gray-300 flex items-center justify-center text-white text-lg font-bold">
                ?
              </div>
            )}
          </div>
          {/* Text */}
          <div className="flex-1 min-w-0">
            <p className="font-serif text-xl font-semibold text-text-dark">My child's story</p>
            <p className="text-lg text-text-medium">Saved into their life story — I'm writing it for them</p>
          </div>
          {/* Radio dot */}
          <RadioDot
            selected={tier === 'child'}
            accentBorder={accentClasses.border}
            accentBg={accentClasses.bgSolid}
          />
        </button>

        {/* ── Expanded child list ─────────────────────────────── */}
        {tier === 'child' && childrenList.length > 0 && (
          <div className="pt-4 mt-0 mx-5 mb-5 border-t-2 border-opacity-30 border-current" style={{ borderColor: 'inherit' }}>
            {/* Header row */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-lg font-semibold text-text-medium">Which children?</span>
              {childrenList.length > 1 && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onToggleAll(); }}
                  className={`px-4 py-2 rounded-full border-2 text-base font-semibold cursor-pointer transition-colors ${
                    allSelected
                      ? `${accentClasses.border} ${accentClasses.bg} ${accentClasses.color}`
                      : 'border-border-gray bg-white text-text-medium'
                  }`}
                >
                  {allSelected ? '\u2713 All selected' : 'Select all'}
                </button>
              )}
            </div>
            {/* Child rows */}
            <div className="space-y-2">
              {childrenList.map((child) => (
                <ChildCheckboxRow
                  key={child.id}
                  child={child}
                  checked={selectedChildIds.includes(child.id)}
                  onToggle={onToggleChild}
                  accentClasses={accentClasses}
                />
              ))}
            </div>
            {/* Warning nudge when none selected */}
            {selectedChildIds.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-xl p-3 text-base text-yellow-800 mt-3">
                &#9888;&#65039; Please select at least one child to continue
              </div>
            )}
          </div>
        )}

        {/* No children message */}
        {tier === 'child' && childrenList.length === 0 && (
          <div className="px-5 pb-5">
            <p className="text-base text-text-medium">
              No children found in your family. Add a child from the Family Hub first.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhoseStorySelector;
